import { Box, Stack, Typography, useTheme } from "@mui/material";
import { PieChart } from "@mui/x-charts";
import { clamp } from "lodash-es";
import type { ReactElement } from "react";
import {
	Resources,
	useResource,
} from "web_myplann/Infrastructure/Translations/Resources";
import { moneyFormatter } from "web_myplann/Shared/Utils/moneyFormatters";

type PieChartData = { value: number; label: string; color?: string };

type Props = {
	data: PieChartData[];
	baseColor?: string;
	centerElement?: ReactElement;
};

const hexToRgb = (hex: string): { r: number; g: number; b: number } | null => {
	const normalized = hex.trim().replace(/^#/, "");
	if (normalized.length === 3) {
		const r = Number.parseInt(normalized[0] + normalized[0], 16);
		const g = Number.parseInt(normalized[1] + normalized[1], 16);
		const b = Number.parseInt(normalized[2] + normalized[2], 16);
		return { r, g, b };
	}
	if (normalized.length === 6) {
		const r = Number.parseInt(normalized.slice(0, 2), 16);
		const g = Number.parseInt(normalized.slice(2, 4), 16);
		const b = Number.parseInt(normalized.slice(4, 6), 16);
		return { r, g, b };
	}
	return null;
};

const rgbToHex = ({ r, g, b }: { r: number; g: number; b: number }): string => {
	const toHex = (n: number) => n.toString(16).padStart(2, "0");
	return `#${toHex(clamp(Math.round(r), 0, 255))}${toHex(clamp(Math.round(g), 0, 255))}${toHex(clamp(Math.round(b), 0, 255))}`;
};

const lightenTowardsWhite = (hex: string, ratio: number): string => {
	const rgb = hexToRgb(hex);
	if (!rgb) return hex;
	const t = clamp(ratio, 0, 1);
	const r = (1 - t) * rgb.r + t * 255;
	const g = (1 - t) * rgb.g + t * 255;
	const b = (1 - t) * rgb.b + t * 255;
	return rgbToHex({ r, g, b });
};

const mapSeriesData = (items: PieChartData[], baseColor?: string) => {
	if (!baseColor) return items;
	const n = items.length;
	if (n === 0) return items;
	const maximumValueIndex = items.reduce(
		(currentMaximumIndex, item, itemIndex, allItems) =>
			item.value > allItems[currentMaximumIndex].value
				? itemIndex
				: currentMaximumIndex,
		0,
	);
	const maxLighten = 0.6;
	let k = 0;
	return items.map((item, itemIndex) => {
		if (itemIndex === maximumValueIndex) return { ...item, color: baseColor };
		k += 1;
		const ratio = (k / n) * maxLighten;
		return { ...item, color: lightenTowardsWhite(baseColor, ratio) };
	});
};

export const MyPlannPieChart = ({ data, baseColor, centerElement }: Props) => {
	const theme = useTheme();
	const { t } = useResource();

	const seriesData = mapSeriesData(data, baseColor);

	return (
		<Stack width="100%">
			<Box position="relative" width="100%" height={250}>
				<PieChart
					height={250}
					margin={{ bottom: 8 }}
					series={[{ data: seriesData, innerRadius: 90 }]}
					slotProps={{
						pieArc: { strokeWidth: 0, stroke: "none" },
						tooltip: { trigger: "none" },
					}}
					hideLegend
				/>
				{centerElement && (
					<Box
						position="absolute"
						top="50%"
						left="50%"
						sx={{ transform: "translate(-50%, -50%)" }}
					>
						{centerElement}
					</Box>
				)}
			</Box>
			<Stack mt={1.6}>
				<Stack gap={0.4}>
					{seriesData.map((d) => (
						<Stack
							key={d.label}
							direction="row"
							alignItems="center"
							justifyContent="space-between"
						>
							<Stack direction="row" alignItems="center" gap={0.6}>
								<Stack
									width={10}
									height={10}
									borderRadius={0.8}
									bgcolor={d.color}
								/>
								<Typography
									variant="deprecatedBody1"
									color={theme.palette.deprecated.grey[500]}
								>
									{d.label}
								</Typography>
							</Stack>
							<Typography
								variant="deprecatedBody2"
								color={theme.palette.deprecated.grey[700]}
							>
								{moneyFormatter(d.value, t(Resources.Common.CurrencySign))}
							</Typography>
						</Stack>
					))}
				</Stack>
			</Stack>
		</Stack>
	);
};
